import React, { useMemo, useRef, useState } from 'react'
import copy from 'copy-to-clipboard'
import styled from 'styled-components'
import { Check, Copy } from 'react-feather'

import toCopyableText from 'utils/toCopyableText'

const CheckIcon = styled(Check)`
	margin-right: 5px;
`

const CopyIcon = styled(Copy)`
	margin-left: 9px;
	position: relative;
	top: 0.5px;
`

const HiddenInput = styled.input`
	position: absolute;
	left: -10000px;
	width: 1px;
	height: 1px;
`

const StyledTooltip = styled.div`
	position: absolute;
	top: -24px;
	left: 50%;
	transform: translateX(-50%);
	white-space: nowrap;
	opacity: ${({ visible }) => (visible ? '1' : '0')};
	transition:
		opacity 0.3s ease,
		background 0.3s ease;

	height: 20px;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 4px 8px;
	border-radius: 2px;
	background-color: ${({ success }) => (success ? 'var(--color-darkGreen2)' : 'var(--color-darkGrey)')};
	color: white;
	font-size: 11px;
	font-weight: 500;
	box-shadow:
		0px 8px 12px rgba(0, 0, 0, 0.15),
		0px 0px 1px rgba(0, 0, 0, 0.3);

	&:after {
		display: block;
		content: '';
		position: absolute;
		bottom: -3px;
		left: calc(50% - 4px);
		background-color: ${({ success }) => (success ? 'var(--color-darkGreen2)' : 'var(--color-darkGrey)')};
		width: 6px;
		height: 6px;
		transform: rotate(45deg);
		transition: background 0.3s ease;
	}
`

const Wrapper = styled.span`
	cursor: pointer;
	position: relative;
`

const Tooltip = ({ visible, success }) => (
	<StyledTooltip visible={visible} success={success}>
		{success ? (
			<>
				<CheckIcon size={12} /> Copied
			</>
		) : (
			<>Click to copy</>
		)}
	</StyledTooltip>
)

const Copyable = ({ children, value }) => {
	const copyRef = useRef()
	const [copySuccess, setCopySuccess] = useState(false)
	const [isTooltipVisible, setIsTooltipVisible] = useState(false)

	const copyToClipboard = () => {
		if (typeof document !== 'undefined' && copyRef.current) {
			copy(copyRef.current.value)
			setCopySuccess(true)
		}
	}

	const textToCopy = useMemo(() => {
		return value || toCopyableText(children)
	}, [value, children])

	const hideTooltip = () => {
		setIsTooltipVisible(false)
		setTimeout(() => {
			setCopySuccess(false)
		}, 500)
	}

	return (
		<>
			<Wrapper
				role='button'
				onClick={copyToClipboard}
				onMouseEnter={() => {
					setIsTooltipVisible(true)
				}}
				onMouseLeave={hideTooltip}
			>
				{children}
				<Tooltip success={copySuccess} visible={isTooltipVisible} />
				<CopyIcon size={12} />
			</Wrapper>
			<HiddenInput ref={copyRef} readOnly value={textToCopy} />
		</>
	)
}

export default Copyable
